import notification from "antd/es/notification";
import { partition } from "lodash-es";
import { defineMessages } from "react-intl";
import { Action } from "redux";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel, {
  Condition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";

import SchedulerViewState from "@app/component/view/SchedulerView/types/SchedulerViewState";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import getPlannerRequestRange from "@app/scene/activity/utils/getPlannerRequestRange";
import { callApi } from "@app/store/api/callApi";
import { setPlannerQuery } from "@app/store/chat/viewContext/actions";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getCurrentUserId, getOrganization, getOrganizationId } from "@app/store/iam";
import { getSelectedFilter } from "@app/store/savedFilters";
import { initializeSavedFiltersForView } from "@app/store/savedFilters/actions";
import { onSetUserColors } from "@app/store/userColor/sagas";
import { isActivity } from "@app/util/activity/assert";
import getParentActivitiesFilter from "@app/util/activity/getParentActivitiesFilter";
import toContextDateRange from "@app/util/chat/plannerContext/toContextDateRange";
import { getWeekStart } from "@app/util/dates";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
  extractSimpleConditionForFieldWithinTag,
} from "@app/util/filters/utils";
import { activityLayoutModel } from "@app/util/layout/impl";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  applySchedulerViewSettings,
  exportSchedulerActivities,
  fetchSchedulerActivities,
  initializeSchedulerView,
  moveResizeActivity,
} from "./actions";
import { getSchedulerViewState, getSchedulerViewTotalFilteredRecordsCount } from "./selectors";

export const SCHEDULER_VIEW_STATE = "activity/schedulerView";

const messages = defineMessages({
  filtersResetNotification: {
    id: "activities.schedulerView.loadingTimeout.notification",
    defaultMessage: "Your applied filters have been reset, as the page was not loaded in time.",
    description: "A notification shown when scheduler cannot load in given time",
  },
});

export function* onInitializeSchedulerView() {
  const savedFilterGetter: (entityType: EntityType) => SavedFilter | undefined =
    yield select(getSelectedFilter);
  const savedFilter = savedFilterGetter(EntityType.ACTIVITY);
  if (!savedFilter) {
    yield put(
      initializeSavedFiltersForView({
        entityType: EntityType.ACTIVITY,
        viewKey: SCHEDULER_VIEW_STATE,
      })
    );
  }
  yield put(initializeSchedulerView.success());
  // now fetch initial portion of activities. Not passing any viewState, because we don't need any changes there
  yield put(fetchSchedulerActivities.request({ viewState: {} }));
}

const getSchedulerRequestFilters = (
  viewState: SchedulerViewState,
  ignoreFilters: boolean = false
): PlatformFilterModel => {
  // Add special filters for the date range.
  // By default, we only keep startAt in filter model, but in fact we should send more details to
  // the backend to get appropriate result. First, we need to generate range for the startAt
  // field. Second, we're not only interested in activities which start in the generated timeframe, but
  // also in ones which end there. Thus, we need to add a filter for the endAt field too.
  // And combine both these filters under the $or conjunction.
  // We also have a special processing for quickFilter filter.

  const [quickFilterCondition, filters] = extractSimpleConditionForFieldWithinTag(
    ignoreFilters ? createEmptyFilterModel() : viewState.filter,
    EntityType.ACTIVITY,
    ActivityFieldName.QUICK_FILTER
  );
  const [, finalFilters] = extractSimpleConditionForFieldWithinTag(
    filters,
    EntityType.ACTIVITY,
    ActivityFieldName.START_AT
  );

  const platformFilterModel = convertToPlatformFilterModel(
    finalFilters,
    viewState.columns,
    activityFieldModel
  );

  const range = ignoreFilters
    ? undefined
    : getPlannerRequestRange(viewState.filter, viewState.viewMode);
  if (range) {
    const rangeCondition: Condition = { $gte: range[0], $lte: range[1] };
    if (!platformFilterModel.$and) {
      platformFilterModel.$and = [];
    }
    platformFilterModel.$and!.push({
      $or: [{ startAt: rangeCondition }, { endAt: rangeCondition }],
    });
  }

  const hasQuickSearchFilter = !ignoreFilters && !!quickFilterCondition;
  if (hasQuickSearchFilter) {
    const { value } = quickFilterCondition;
    if (!platformFilterModel.$and) {
      platformFilterModel.$and = [];
    }
    platformFilterModel.$and!.push({
      $or: [
        { name: { $in: value } },
        { "account.name": { $in: value } },
        { "contact.name": { $in: value } },
        { "deal.name": { $in: value } },
      ],
    });
  }
  platformFilterModel.includeAccessStatus = true;
  platformFilterModel.includeNotes = true;
  platformFilterModel.includeCustomFields = true;
  platformFilterModel.includeUsersWithAccess = true;

  const hasRelationshipFilters = (Object.keys(viewState.filter) as ActivityFieldName[]).some(
    (name) =>
      [
        ActivityFieldName.COMPANY,
        ActivityFieldName.DEAL,
        ActivityFieldName.PEOPLE,
        ActivityFieldName.PERSON,
      ].includes(name)
  );
  // We only fetch parent activities for the list view when there are no relationship filters
  if (!hasRelationshipFilters && !hasQuickSearchFilter) {
    if (!platformFilterModel.$and) {
      platformFilterModel.$and = [];
    }
    platformFilterModel.$and!.push(getParentActivitiesFilter());
  }

  return platformFilterModel;
};

export function* onExportSchedulerActivities({
  payload: { viewState },
}: ReturnType<typeof exportSchedulerActivities>) {
  const total: number = yield select(getSchedulerViewTotalFilteredRecordsCount);
  yield put(
    exportEntities.request({
      entityType: EntityType.ACTIVITY,
      platformRequest: {
        $filters: getSchedulerRequestFilters(viewState),
        $order: convertToPlatformSortModel(viewState.sort),
      },
      total,
      viewState: { columns: viewState.columns },
    })
  );
}

export function* onFetchSchedulerActivities({
  payload,
}: ReturnType<typeof fetchSchedulerActivities.request>) {
  try {
    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applySchedulerViewSettings(payload.viewState));
    }
    const organizationId: Organization["id"] = yield select(getOrganizationId);

    const viewState: SchedulerViewState = yield select(getSchedulerViewState);

    if (!payload.fetchOnlyWithoutFilters && !payload.updateOnly) {
      localSettings.setViewSettings(viewState, SCHEDULER_VIEW_STATE);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.viewState.range
        ? payload.viewState.range.startRow
        : viewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.viewState.range
        ? payload.viewState.range.endRow - payload.viewState.range.startRow
        : viewState.range.endRow - viewState.range.startRow;

    const requestPayload = {
      $filters: getSchedulerRequestFilters(viewState, payload.fetchOnlyWithoutFilters),
      $limit,
      $offset,
      $order: convertToPlatformSortModel(viewState.sort),
    };

    // What the request used, which the timeout fallback below replaces.
    let fetchedViewState = viewState;
    let response: ListResponse<Activity> = {
      accessible: 0,
      count: 0,
      data: [],
      limit: 0,
      offset: 0,
      total: 0,
    };

    try {
      response = yield callApi("fetchActivities", organizationId, requestPayload);
    } catch (error) {
      if (!(error instanceof ApiError && error.message.startsWith("timeout"))) {
        throw error;
      }

      const currentUserId: User["id"] = yield select(getCurrentUserId);
      const filter = createAndCondition([
        createAndCondition(
          [
            createSimpleCondition(
              ActivityFieldName.START_AT,
              FilterOperator.ON,
              getWeekStart(Date.now())
            ),
            createSimpleCondition(ActivityFieldName.ASSIGNEE, FilterOperator.IN_ANY, [
              currentUserId,
            ]),
          ],
          EntityType.ACTIVITY
        ),
      ]);

      const updatedViewState: SchedulerViewState = { ...viewState, filter };
      yield put(applySchedulerViewSettings(updatedViewState));
      fetchedViewState = updatedViewState;

      const requestPayload = {
        $filters: getSchedulerRequestFilters(updatedViewState, payload.fetchOnlyWithoutFilters),
        $limit,
        $offset,
        $order: convertToPlatformSortModel(updatedViewState.sort),
      };

      notification.warning({
        message: i18nService.formatMessage(
          messages.filtersResetNotification,
          "Your applied filters have been reset, as the page was not loaded in time"
        ),
      });
      response = yield callApi("fetchActivities", organizationId, requestPayload);
    }

    const [activities] = partition(response.data, isActivity);

    if (payload.dataCallback) {
      yield call(payload.dataCallback, { ...response, data: activities });
    }
    yield put(
      fetchSchedulerActivities.success({
        activities,
        totalFilteredRecords: activities.length,
        totalRecords: response.accessible,
      })
    );
    // The chat reads the query that just succeeded, not the one the rep is still editing.
    if (!payload.fetchOnlyWithoutFilters) {
      yield put(
        setPlannerQuery({
          activityIds: activities.map(({ id }) => id),
          dateRange: toContextDateRange(
            getPlannerRequestRange(fetchedViewState.filter, fetchedViewState.viewMode)
          ),
          filter: fetchedViewState.filter,
          savedFilterId: fetchedViewState.selectedSavedFilterId,
          totalCount: activities.length,
          view: "scheduler",
          viewMode: fetchedViewState.viewMode,
        })
      );
    }
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }

    yield put(fetchSchedulerActivities.failure());
    yield put(handleError({ error }));
  }
}

export function* onMoveResizeActivity({ payload }: ReturnType<typeof moveResizeActivity.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload.activity).id;
    const { files, ...activity } = payload.activity; // excluding files field from request

    const updatedActivity: Activity = yield callApi(
      "updateActivity",
      organization.id,
      layoutId,
      activity
    );
    yield put(moveResizeActivity.success(updatedActivity));
    yield put(fetchSchedulerActivities.request({ viewState: {} })); // just reload, don't change view
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback, payload.activity);
    }
    yield put(moveResizeActivity.failure());
    yield put(handleError({ error }));
  }
}

export function* onApplySchedulerViewSettings({
  payload,
}: ReturnType<typeof applySchedulerViewSettings>) {
  yield call(onSetUserColors, payload);
}

export function* schedulerSaga() {
  yield takeEvery(initializeSchedulerView.request, onInitializeSchedulerView);
  yield takeLatest(
    (action: Action) =>
      isActionOf(fetchSchedulerActivities.request)(action) && !action.payload.updateOnly,
    onFetchSchedulerActivities
  );
  yield takeEvery(
    (action: Action) =>
      isActionOf(fetchSchedulerActivities.request)(action) && !!action.payload.updateOnly,
    onFetchSchedulerActivities
  );
  yield takeEvery(moveResizeActivity.request, onMoveResizeActivity);
  yield takeEvery(applySchedulerViewSettings, onApplySchedulerViewSettings);
  yield takeEvery(exportSchedulerActivities, onExportSchedulerActivities);
}
